import { TeacherStatus } from 'constants/common';
import { useState } from 'react';
import { Outlet } from 'react-router-dom';
// @mui
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
// hooks
import { useSettings, useResponsive, useCollapseDrawer } from '@onedesign/ui';

// config
import { useProfileService } from 'services/ProfileService';
import { HEADER, NAVBAR } from '../../config';
//
import DashboardHeader from './header';
import NavbarVertical from './navbar/NavbarVertical';
import NavbarHorizontal from './navbar/NavbarHorizontal';
import ContractUpdateDialog from './navbar/components/contractUpdateDialog/ContractUpdateDialog';

// ----------------------------------------------------------------------

type MainStyleProps = {
  collapseClick: boolean;
};

const MainStyle = styled('main', {
  shouldForwardProp: prop => prop !== 'collapseClick',
})<MainStyleProps>(({ collapseClick, theme }) => ({
  flexGrow: 1,
  /*
    撐滿視窗高度並讓子項能用 flex 分配剩餘空間 —— 需要「內容區填滿畫面、內部自己捲動」
    的頁面（例如排課請求）才有辦法把高度算出來，不必寫死像素或量測位置。
    paddingTop 會被合約警告 banner 動態改寫（見 header/Banner），用 flex 鏈才不會算錯。

    ⚠️ 這是所有頁面共用的容器。改成 flex container 之後子項之間不再有 margin collapse，
    原本靠相鄰 margin 疊合取得間距的頁面可能會變寬一點。
  */
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  paddingTop: HEADER.MOBILE_HEIGHT + 24,
  paddingBottom: 40,
  paddingLeft: 16,
  paddingRight: 16,
  [theme.breakpoints.up('lg')]: {
    paddingTop: HEADER.DASHBOARD_DESKTOP_HEIGHT + 24,
    paddingBottom: 40,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH}px)`,
    transition: theme.transitions.create('margin-left', {
      duration: theme.transitions.duration.shorter,
    }),
    ...(collapseClick && {
      marginLeft: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
    }),
  },
  [theme.breakpoints.down('md')]: {
    paddingLeft: 0,
    paddingRight: 0,
  },
}));

// ----------------------------------------------------------------------

export default () => {
  const { collapseClick, isCollapse } = useCollapseDrawer();
  const { profile } = useProfileService();

  const { themeLayout } = useSettings();

  const isDesktop = useResponsive('up', 'lg');

  const [open, setOpen] = useState(false);

  const isTeacherUnverified: boolean =
    profile.status !== '' && profile.status !== undefined
      ? profile.status === TeacherStatus.UNVERIFIED
      : false;
  const verticalLayout = themeLayout === 'vertical';

  if (verticalLayout) {
    return (
      <>
        <DashboardHeader
          onOpenSidebar={() => setOpen(true)}
          verticalLayout={verticalLayout}
        />

        {isDesktop ? (
          <NavbarHorizontal />
        ) : (
          <NavbarVertical
            isOpenSidebar={open}
            onCloseSidebar={() => setOpen(false)}
          />
        )}

        <Box
          component="main"
          sx={{
            px: { lg: 2 },
            pt: {
              xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
              lg: `${HEADER.DASHBOARD_DESKTOP_HEIGHT + 80}px`,
            },
            pb: {
              xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
              lg: `${HEADER.DASHBOARD_DESKTOP_HEIGHT + 24}px`,
            },
          }}
        >
          {!isTeacherUnverified && <ContractUpdateDialog />}
          <Outlet context={{}} />
        </Box>
      </>
    );
  }

  return (
    <Box
      sx={{
        display: { lg: 'flex' },
        minHeight: { lg: 1 },
      }}
    >
      <DashboardHeader
        isCollapse={isCollapse}
        onOpenSidebar={() => setOpen(true)}
      />

      <NavbarVertical
        isOpenSidebar={open}
        onCloseSidebar={() => setOpen(false)}
      />

      <MainStyle
        collapseClick={collapseClick}
        className="showHiddenWarningBanner"
      >
        {!isTeacherUnverified && <ContractUpdateDialog />}
        <Outlet context={{}} />
      </MainStyle>
    </Box>
  );
};
